
import type { MortgageData } from '../../CalculatorTabs';
import type { LoanProgram } from './mortgageUtils';

export function generateRealTimeLoanPrograms(mortgageData: MortgageData): LoanProgram[] {
  const { creditScore, isVeteran, downPaymentPercentage, loanType, vaStatus, vaDisabilityStatus, interestRate, homebuyingHistory } = mortgageData;
  
  const programs: LoanProgram[] = [];
  
  // Use user's interest rate as baseline with credit score adjustments
  const baseRate = interestRate;
  const creditScoreAdjustment = creditScore >= 740 ? -0.25 : creditScore >= 680 ? 0 : 0.25;
  const conventionalRate = Math.max(baseRate + creditScoreAdjustment, 3.0);
  
  // Conventional Loans
  programs.push({
    id: 'conventional-standard',
    name: 'Conventional Fixed Rate',
    baseRate: conventionalRate,
    downPaymentPercent: Math.max(downPaymentPercentage, 5),
    eligibilityScore: creditScore >= 620 ? 85 : 60,
    recommended: loanType === 'conventional' && creditScore >= 680,
    eligibleForUser: creditScore >= 620
  });

  // FHA Loans (typically 0.25% lower than conventional)
  const fhaRate = Math.max(baseRate - 0.25, 3.0);
  programs.push({
    id: 'fha-standard',
    name: 'FHA Fixed Rate',
    baseRate: fhaRate,
    downPaymentPercent: 3.5,
    eligibilityScore: creditScore >= 580 ? 90 : 70,
    recommended: loanType === 'fha' || (creditScore < 680 && downPaymentPercentage < 10),
    eligibleForUser: creditScore >= 500
  });

  // VA Loans (if veteran) - Always generate core VA programs plus variants
  if (isVeteran) {
    const vaBaseRate = Math.max(baseRate - 0.5, 3.0);
    const vaStandardRate = Math.max(baseRate - 0.25, 3.0);
    const isFirstTimeVA = homebuyingHistory === 'firstTime';
    const texasBenefits = mortgageData.state === 'TX' ? ['TX Property tax exemptions'] : [];
    const texaBenefits100 = mortgageData.state === 'TX' ? ['TX Property tax 100% EXEMPT'] : [];
    
    // Always add VA First-Time programs for eligible veterans
    if (isFirstTimeVA) {
      programs.push({
        id: 'va-first-time',
        name: 'VA First-Time Homebuyer',
        baseRate: Math.max(vaBaseRate - 0.15, 3.0),
        downPaymentPercent: 0,
        eligibilityScore: 100,
        recommended: true,
        eligibleForUser: true,
        description: 'Special VA program for first-time homebuyers',
        benefits: ['No down payment', 'No PMI', 'First-time buyer benefits', 'Competitive rates', 'Assumable loan']
      });
    }

    // Always add VA 10%-90% Disability program
    programs.push({
      id: 'va-10-90-disability',
      name: 'VA 10%-90% Disability',
      baseRate: vaBaseRate,
      downPaymentPercent: 0,
      eligibilityScore: 96,
      recommended: vaDisabilityStatus === 'partial',
      eligibleForUser: true,
      description: 'VA loan for veterans with 10%-90% disability rating',
      benefits: ['No down payment', 'No PMI', 'NO funding fee', 'Assumable loan', ...texasBenefits]
    });

    // Always add VA 100% Disability program
    const va100Rate = Math.max(baseRate - 0.75, 3.0);
    programs.push({
      id: 'va-100-disability',
      name: 'VA 100% Disability',
      baseRate: va100Rate,
      downPaymentPercent: 0,
      eligibilityScore: 100,
      recommended: vaDisabilityStatus === 'full',
      eligibleForUser: true,
      description: 'Premium VA loan for 100% disabled veterans',
      benefits: ['No down payment', 'No PMI', 'NO funding fee', 'Best VA rates', 'Assumable loan', ...texaBenefits100]
    });

    // Always add Standard VA program
    programs.push({
      id: 'va-standard',
      name: 'VA Standard',
      baseRate: vaStandardRate,
      downPaymentPercent: 0,
      eligibilityScore: 95,
      recommended: !vaDisabilityStatus && !isFirstTimeVA,
      eligibleForUser: true,
      description: 'Standard VA loan for eligible veterans',
      benefits: ['No down payment', 'No PMI', 'Competitive rates', 'Assumable loan']
    });

    // Optional: Add specific status-based variants if status is known
    if (vaStatus === 'activeDuty') {
      programs.push({
        id: 'va-active-duty',
        name: 'VA Active Duty',
        baseRate: vaBaseRate,
        downPaymentPercent: 0,
        eligibilityScore: 98,
        recommended: true,
        eligibleForUser: true,
        description: 'Special rate for active duty service members',
        benefits: ['No down payment', 'No PMI', '2.15% funding fee', 'Assumable loan']
      });
    }

    if (vaStatus === 'separatedRetired') {
      programs.push({
        id: 'va-separated-retired',
        name: 'VA Separated/Retired',
        baseRate: vaStandardRate,
        downPaymentPercent: 0,
        eligibilityScore: 95,
        recommended: true,
        eligibleForUser: true,
        description: 'Standard VA loan for separated or retired veterans',
        benefits: ['No down payment', 'No PMI', '2.3% funding fee', 'Assumable loan']
      });
    }
  }

  // Jumbo Loans (typically 0.25% higher than conventional)
  if (mortgageData.homePrice > 766550) {
    const jumboRate = conventionalRate + 0.25 + (creditScore >= 740 ? 0 : 0.25);
    programs.push({
      id: 'jumbo-conventional',
      name: 'Jumbo Fixed Rate',
      baseRate: jumboRate,
      downPaymentPercent: Math.max(downPaymentPercentage, 10),
      eligibilityScore: creditScore >= 700 ? 80 : 65,
      recommended: creditScore >= 720,
      eligibleForUser: creditScore >= 680
    });
  }

  // USDA (typically 0.2% lower than conventional)
  const usdaRate = Math.max(baseRate - 0.2, 3.0);
  programs.push({
    id: 'usda-standard',
    name: 'USDA Rural Development',
    baseRate: usdaRate,
    downPaymentPercent: 0,
    eligibilityScore: 75,
    recommended: false, // Would need location data to properly recommend
    eligibleForUser: creditScore >= 640
  });

  // First-time homebuyer programs (typically 0.3% lower than conventional)
  if (mortgageData.isFirstTimeHomebuyer) {
    const firstTimeBuyerRate = Math.max(baseRate - 0.3, 3.0);
    programs.push({
      id: 'first-time-buyer',
      name: 'First-Time Homebuyer Special',
      baseRate: firstTimeBuyerRate,
      downPaymentPercent: 3,
      eligibilityScore: 88,
      recommended: true,
      eligibleForUser: true
    });
  }

  return programs.sort((a, b) => {
    // Sort by recommended first (VA loans prioritized for veterans), then by eligibility score
    if (a.recommended && !b.recommended) return -1;
    if (!a.recommended && b.recommended) return 1;
    return b.eligibilityScore - a.eligibilityScore;
  });
}
